/**
 * 問い合わせ管理 API クライアント
 */

import { AuthenticatedApiClient } from '../client';
import type {
  CreateAdminMessageRequest,
  InquiryAttachmentUrlResponse,
  InquiryDetail,
  InquiryListResponse,
  InquiryUpdateResponse,
  ListInquiriesParams,
  UpdateInquiryCategoryRequest,
  UpdateInquiryStatusRequest,
} from '@/lib/types/mise/inquiry';

function buildQueryString(params: ListInquiriesParams): string {
  const search = new URLSearchParams();
  if (params.source_product) search.set('source_product', params.source_product);
  if (params.status) search.set('status', params.status);
  if (params.category) search.set('category', params.category);
  if (params.company_id) search.set('company_id', params.company_id);
  if (params.account_id) search.set('account_id', params.account_id);
  if (params.seller_id) search.set('seller_id', params.seller_id);
  if (params.q) search.set('q', params.q);
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.sort_by) search.set('sort_by', params.sort_by);
  if (params.sort_order) search.set('sort_order', params.sort_order);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function listInquiriesAuthenticated(
  authHeaders: Record<string, string>,
  params: ListInquiriesParams = {},
): Promise<InquiryListResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<InquiryListResponse>(`/mise/inquiries${buildQueryString(params)}`);
  } catch (error) {
    console.error('Failed to list inquiries:', error);
    throw error;
  }
}

export async function getInquiryAuthenticated(
  inquiryId: string,
  authHeaders: Record<string, string>,
): Promise<InquiryDetail> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<InquiryDetail>(`/mise/inquiries/${inquiryId}`);
  } catch (error) {
    console.error(`Failed to fetch inquiry ${inquiryId}:`, error);
    throw error;
  }
}

export async function createAdminMessageAuthenticated(
  inquiryId: string,
  request: CreateAdminMessageRequest,
  authHeaders: Record<string, string>,
): Promise<{ id: string }> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.post<{ id: string }>(`/mise/inquiries/${inquiryId}/messages`, request);
  } catch (error) {
    console.error(`Failed to create admin message on ${inquiryId}:`, error);
    throw error;
  }
}

export async function updateInquiryStatusAuthenticated(
  inquiryId: string,
  request: UpdateInquiryStatusRequest,
  authHeaders: Record<string, string>,
): Promise<InquiryUpdateResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.put<InquiryUpdateResponse>(`/mise/inquiries/${inquiryId}/status`, request);
  } catch (error) {
    console.error(`Failed to update status on ${inquiryId}:`, error);
    throw error;
  }
}

export async function updateInquiryCategoryAuthenticated(
  inquiryId: string,
  request: UpdateInquiryCategoryRequest,
  authHeaders: Record<string, string>,
): Promise<InquiryUpdateResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.put<InquiryUpdateResponse>(
      `/mise/inquiries/${inquiryId}/category`,
      request,
    );
  } catch (error) {
    console.error(`Failed to update category on ${inquiryId}:`, error);
    throw error;
  }
}

export async function getInquiryAttachmentUrlAuthenticated(
  inquiryId: string,
  attachmentId: string,
  authHeaders: Record<string, string>,
): Promise<InquiryAttachmentUrlResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<InquiryAttachmentUrlResponse>(
      `/mise/inquiries/${inquiryId}/attachments/${attachmentId}`,
    );
  } catch (error) {
    console.error(
      `Failed to get presigned URL for attachment ${attachmentId} on ${inquiryId}:`,
      error,
    );
    throw error;
  }
}
